import { RivenditoreStoreLocatorNode } from '@gql/graphql-get'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'

export interface StoreLocatorViewState {
  latitude: number
  longitude: number
  zoom: number
}

export interface StoreLocatorState {
  address?: string
  locationPinJson?: any
  mapNavItem: string
  openResults: boolean
  rivenditoriLimit: number
  rivenditoriListKeys?: string[]
  rivenditoriListPks: number[]
  searchedCoords: number[]
  selectedRivenditoreKey?: string
  selectedRivenditorePk?: number
  viewState?: StoreLocatorViewState
  listScrollTop: number
}

interface StoreLocatorContextValue {
  storeLocatorState: StoreLocatorState | null
  setStoreLocatorState: (state: StoreLocatorState | null) => void
  clearStoreLocatorState: () => void
}

export const StoreLocatorContext = React.createContext<StoreLocatorContextValue>({
  storeLocatorState: null,
  setStoreLocatorState: () => {},
  clearStoreLocatorState: () => {},
})

export const getRivenditoreStoreLocatorKey = (rivenditore?: RivenditoreStoreLocatorNode | null) =>
  [
    rivenditore?.url || '',
    rivenditore?.pk || '',
    rivenditore?.latitudine || '',
    rivenditore?.longitudine || '',
  ].join('|')

const getStoreLocatorPathSegments = (url: string) => {
  const pathname = url.split('?')[0].split('#')[0].replace(/\/$/, '')
  const segments = pathname.split('/').filter(Boolean)
  const [firstSegment] = segments

  if (firstSegment === 'int' || /^[a-z]{2}(?:-[a-z]{2})?$/.test(firstSegment)) {
    return segments.slice(1)
  }

  return segments
}

export const isStoreLocatorListingRoute = (url: string) => {
  const segments = getStoreLocatorPathSegments(url)

  return segments[0] === 'store-locator' && segments[1] !== 'dealer' && segments.length <= 3
}

export const isStoreLocatorDealerRoute = (url: string) => {
  const segments = getStoreLocatorPathSegments(url)

  return (
    segments[0] === 'store-locator' &&
    segments[1] === 'dealer' &&
    !!segments[2] &&
    segments.length === 3
  )
}

const shouldKeepStoreLocatorState = (fromUrl: string, toUrl: string) =>
  (isStoreLocatorListingRoute(fromUrl) && isStoreLocatorDealerRoute(toUrl)) ||
  (isStoreLocatorDealerRoute(fromUrl) && isStoreLocatorListingRoute(toUrl))

const isValidNumber = (value?: number) => typeof value === 'number' && Number.isFinite(value)

const isValidViewState = (viewState?: StoreLocatorViewState) => {
  if (!viewState) return false

  return (
    isValidNumber(viewState.latitude) &&
    isValidNumber(viewState.longitude) &&
    isValidNumber(viewState.zoom) &&
    viewState.latitude >= -90 &&
    viewState.latitude <= 90 &&
    viewState.longitude >= -180 &&
    viewState.longitude <= 180
  )
}

export const isRestorableStoreLocatorState = (state?: StoreLocatorState | null) => {
  if (!state?.openResults || !isValidViewState(state.viewState)) return false

  return (
    !!state.selectedRivenditoreKey ||
    !!state.selectedRivenditorePk ||
    !!state.rivenditoriListKeys?.length ||
    !!state.rivenditoriListPks?.length ||
    state.searchedCoords?.length === 2
  )
}

export const getSavedRivenditoriList = (
  rivenditori: RivenditoreStoreLocatorNode[],
  savedPks?: number[],
  savedKeys?: string[]
) => {
  if (savedKeys?.length) {
    const rivenditoriByKey = new Map(
      rivenditori.map((rivenditore) => [getRivenditoreStoreLocatorKey(rivenditore), rivenditore])
    )

    return savedKeys
      .map((key) => rivenditoriByKey.get(key))
      .filter(Boolean) as RivenditoreStoreLocatorNode[]
  }

  if (!savedPks?.length) return []

  const rivenditoriByPk = new Map(rivenditori.map((rivenditore) => [rivenditore.pk, rivenditore]))

  return savedPks
    .map((pk) => rivenditoriByPk.get(pk))
    .filter(Boolean) as RivenditoreStoreLocatorNode[]
}

const StoreLocatorProvider = ({ children }: PropsWithChildren) => {
  const [storeLocatorState, setStoreLocatorState] = useState<StoreLocatorState | null>(null)
  const router = useRouter()
  const currentUrlRef = useRef(router.asPath)

  const clearStoreLocatorState = useCallback(() => setStoreLocatorState(null), [])

  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      if (!shouldKeepStoreLocatorState(currentUrlRef.current, url)) {
        clearStoreLocatorState()
      }
    }

    const handleRouteChangeComplete = (url: string) => {
      currentUrlRef.current = url
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [clearStoreLocatorState, router.events])

  useEffect(() => {
    currentUrlRef.current = router.asPath
  }, [router.asPath])

  return (
    <StoreLocatorContext.Provider
      value={{ clearStoreLocatorState, setStoreLocatorState, storeLocatorState }}
    >
      {children}
    </StoreLocatorContext.Provider>
  )
}

export default StoreLocatorProvider
